
import { Component, Vue, Prop, Watch } from 'vue-property-decorator';
import { StoreModel } from '@/models/store';
import { MeasureModel } from '@/models/measure';
import { MeasureList } from '@/collections/measures';
import { DimensionModel } from '@/models/dimension';
import { FilterColumnList } from '@/collections/filter_columns';
import { FilterColumnModel } from '@/models/filter_column';
import { ProductSegmentModel } from '@/models/product_segment';
import ReportFilter from '@/components/ReportFilter.vue';

interface FilterData {
  filterColumns: FilterColumnList;
  productSegment: ProductSegmentModel;
}

@Component({
  components: {
    ReportFilter
  }
})
export default class AutomationFilterSection extends Vue {
  @Prop({ required: true }) store!: StoreModel;
  @Prop({ required: true }) filterColumns!: FilterColumnList;
  @Prop({ default: null }) productSegmentId!: number | null;

  private measures: MeasureList = new MeasureList();
  private measure: MeasureModel = new MeasureModel();
  private dimension: DimensionModel = new DimensionModel();
  private hasSchemas: boolean = true;
  
  private filterData: FilterData = {
    filterColumns: new FilterColumnList(),
    productSegment: new ProductSegmentModel()
  };
  
  private initFilterColumns: FilterColumnList = new FilterColumnList();
  
  // Flag to prevent recursive updates
  private isUpdating: boolean = false;
  
  // Filter columns specific to product selection
  private filterReportColumns: any[] = [
    // Text-based product attributes
    {
      name: "Product Tags",
      code: "product_tags",
      dataType: "text",
      luisMapping: "product_tags"
    },
    {
      name: "Collections",
      code: "product_collections",
      dataType: "text",
      luisMapping: "product_collections"
    },
    {
      name: "Product",
      code: "Item[Item Description]",
      dataType: "text",
      luisMapping: "by_product"
    },
    {
      name: "SKU",
      code: "product_sku",
      dataType: "text",
      luisMapping: "by_product_sku"
    },
    {
      name: "Product Type",
      code: "product_type",
      dataType: "text",
      luisMapping: "by_product_type"
    },
    {
      name: "Vendor",
      code: "Vendor Name",
      dataType: "text",
      luisMapping: "by_vendor"
    },
    {
      name: "Variant",
      code: "variant",
      dataType: "text",
      luisMapping: "variant"
    },
    {
      name: "Barcode",
      code: "product_barcode",
      dataType: "text",
      luisMapping: "product_barcode"
    },
    {
      name: "Product Status",
      code: "product_status",
      dataType: "text",
      luisMapping: "by_product_status"
    },
    // Metric columns for advanced filtering (e.g., exclude slow-moving products)
    {
      name: "Units Sold per Day",
      code: "[average_daily_units_sold_param]",
      dataType: "decimal",
      luisMapping: "average_daily_units_sold_param"
    },
    {
      name: "Adjusted Units Sold per Day",
      code: "[average_daily_units_sold_sales_trend]",
      dataType: "decimal",
      luisMapping: "average_daily_units_sold_sales_trend"
    },
    {
      name: "Forecast Units Sold per Day",
      code: "[average_daily_units_sold_forecast]",
      dataType: "decimal",
      luisMapping: "average_daily_units_sold_forecast"
    },
    {
      name: "Net Units Sold",
      code: "[sold_quantity_param]",
      dataType: "decimal",
      luisMapping: "sold_quantity_param"
    },
    {
      name: "Reorder Quantity",
      code: "[inventory_replenishment]",
      dataType: "decimal",
      luisMapping: "inventory_replenishment"
    },
    {
      name: "Unit Cost",
      code: "unit_cost",
      dataType: "decimal",
      luisMapping: "unit_cost"
    },
    {
      name: "Lead Time (days)",
      code: "[vendor_lead_time]",
      dataType: "decimal",
      luisMapping: "vendor_lead_time"
    },
    {
      name: "Reorder Cycle (days)",
      code: "[reorder_cycle]",
      dataType: "decimal",
      luisMapping: "reorder_cycle"
    },
    {
      name: "Inventory Quantity",
      code: "[Actual Inventory Quantity]",
      dataType: "decimal",
      luisMapping: "Actual Inventory Quantity"
    },
    {
      name: "Incoming Quantity",
      code: "[incoming_inventory_quantity]",
      dataType: "decimal",
      luisMapping: "incoming_inventory_quantity"
    },
    {
      name: "Safety Stock",
      code: "[safety_stock_level]",
      dataType: "decimal",
      luisMapping: "safety_stock_level"
    },
    {
      name: "Reorder Point",
      code: "[reorder_point]",
      dataType: "decimal",
      luisMapping: "reorder_point"
    },
    {
      name: "Min Order Quantity",
      code: "[min_order_quantity]",
      dataType: "decimal",
      luisMapping: "min_order_quantity"
    },
    {
      name: "Max Order Quantity",
      code: "[max_order_quantity]",
      dataType: "decimal",
      luisMapping: "max_order_quantity"
    },
    {
      name: "Min Inventory Quantity",
      code: "[min_inventory_quantity]",
      dataType: "decimal",
      luisMapping: "min_inventory_quantity"
    },
    {
      name: "Max Inventory Quantity",
      code: "[max_inventory_quantity]",
      dataType: "decimal",
      luisMapping: "max_inventory_quantity"
    }
  ];

  async created() {
    
    await this.initMeasure();
    await this.initializeFilters();
  }

  @Watch('productSegmentId', { immediate: false })
  async onProductSegmentIdChange() {
    if (this.productSegmentId && this.productSegmentId !== this.filterData.productSegment?.id) {
      // Load product segment if ID is provided
      const segment = new ProductSegmentModel();
      segment.id = this.productSegmentId;
      await segment.fetch();
      this.filterData.productSegment = segment;
    } else if (!this.productSegmentId && this.filterData.productSegment?.id) {
      this.filterData.productSegment = new ProductSegmentModel();
    }
  }

  @Watch('filterColumns', { immediate: true, deep: true })
  onFilterColumnsChange(newValue: FilterColumnList) {
    
    // Skip if we're in the middle of an update to prevent loops
    if (this.isUpdating) return;
    
    // Check if this is a real change by comparing JSON representations
    const currentJSON = JSON.stringify(this.filterData.filterColumns?.items || []);
    const newJSON = JSON.stringify(newValue?.items || []);
    
    
    // Update if there's a change OR if it's the initial load (filterData is empty but newValue has items)
    const shouldUpdate = newJSON !== currentJSON && newValue && (newValue.items || currentJSON === '[]');
    
    if (shouldUpdate) {
      // Create a new FilterColumnList instance to avoid mutating the prop
      const newFilterColumns = new FilterColumnList();
      if (newValue.items && newValue.items.length > 0) {
        newFilterColumns.mapData(newValue.items.map(item => item.toJSON()));
      }
      this.filterData.filterColumns = newFilterColumns;
      
      // Also update initFilterColumns for the ReportFilter component
      this.initFilterColumns = new FilterColumnList();
      if (newValue.items && newValue.items.length > 0) {
        this.initFilterColumns.mapData(newValue.items.map(item => item.toJSON()));
      }
      
    }
  }

  private async initializeFilters() {
    // Remove duplicate filter initialization - the @Watch('filterColumns') handles this
    // Only handle productSegmentId initialization here
    
    if (this.productSegmentId) {
      // Load product segment if ID is provided
      const segment = new ProductSegmentModel();
      segment.id = this.productSegmentId;
      await segment.fetch();
      this.filterData.productSegment = segment;
    }
  }

  private async initMeasure() {
    const measureCode = "replenishment_settings";
    this.measure.code = measureCode;
    
    if (this.measures.items.length === 0) {
      await this.measure.fetch();
    } else {
      for (const item of this.measures.items) {
        if (item.code === measureCode) {
          this.measure = item;
          break;
        }
      }
    }
    
    // Set default dimension
    for (const item of this.measure.dimensions.items) {
      if (item.code === "by_variant") {
        this.dimension = item;
        break;
      }
    }
  }

  get activeFilters(): FilterColumnModel[] {
    return this.filterData.filterColumns.items || [];
  }

  private handleFilterUpdate(data: FilterData) {
    this.filterData = data;
    
    // Only emit if there are actual changes to prevent infinite loops
    const currentFiltersJSON = JSON.stringify(this.filterColumns?.items || []);
    const newFiltersJSON = JSON.stringify(data.filterColumns?.items || []);
    
    if (currentFiltersJSON !== newFiltersJSON) {
      // Set flag to prevent recursive updates
      this.isUpdating = true;
      
      // Emit the filter columns
      this.$emit('update:filter-columns', data.filterColumns);
      
      // Reset flag after a tick
      this.$nextTick(() => {
        this.isUpdating = false;
      });
    }
    
    // Only emit segment ID if it changed
    if (this.productSegmentId !== (data.productSegment?.id || null)) {
      // Emit product segment ID separately
      this.$emit('update:productSegmentId', data.productSegment?.id || null);
    }
  }

  private removeFilter(filter: FilterColumnModel) {
    const index = this.filterData.filterColumns.items.findIndex(f => f.code === filter.code);
    if (index > -1) {
      this.filterData.filterColumns.items.splice(index, 1);
      this.handleFilterUpdate(this.filterData);
    }
  }

  private clearProductSegment() {
    this.filterData.productSegment = new ProductSegmentModel();
    this.handleFilterUpdate(this.filterData);
  }

  private getFilterIcon(filter: FilterColumnModel): string {
    const iconMap: { [key: string]: string } = {
      // Text-based columns
      'product_tags': 'mdi-tag-multiple',
      'product_collections': 'mdi-folder-multiple',
      'Item[Item Description]': 'mdi-cube',
      'product_sku': 'mdi-barcode',
      'product_type': 'mdi-shape',
      'Vendor Name': 'mdi-store',
      'variant': 'mdi-cube-outline',
      'product_barcode': 'mdi-barcode-scan',
      'product_status': 'mdi-list-status',
      // Metric columns
      '[average_daily_units_sold_param]': 'mdi-chart-line',
      '[average_daily_units_sold_sales_trend]': 'mdi-trending-up',
      '[average_daily_units_sold_forecast]': 'mdi-chart-timeline-variant',
      '[sold_quantity_param]': 'mdi-cart',
      '[inventory_replenishment]': 'mdi-package-variant',
      'unit_cost': 'mdi-currency-usd',
      '[vendor_lead_time]': 'mdi-clock-outline',
      '[reorder_cycle]': 'mdi-autorenew',
      '[Actual Inventory Quantity]': 'mdi-warehouse',
      '[incoming_inventory_quantity]': 'mdi-truck-delivery',
      '[safety_stock_level]': 'mdi-shield-check',
      '[reorder_point]': 'mdi-alert-circle-outline',
      '[min_order_quantity]': 'mdi-arrow-collapse-down',
      '[max_order_quantity]': 'mdi-arrow-collapse-up',
      '[min_inventory_quantity]': 'mdi-tray-arrow-down',
      '[max_inventory_quantity]': 'mdi-tray-arrow-up'
    };

    return iconMap[filter.code] || 'mdi-filter';
  }

  private formatFilterValue(filter: FilterColumnModel): string {
    if (!filter.value || filter.value.length === 0) return 'None';

    // Get operator display text for numeric filters
    const operatorMap: { [key: string]: string } = {
      'eq': '=',
      'ne': '!=',
      'lt': '<',
      'lte': '<=',
      'gt': '>',
      'gte': '>=',
      'contains': 'contains',
      'notContains': 'not contains'
    };

    const values = filter.value;
    const operator = filter.operator;
    const isNumericOperator = ['lt', 'lte', 'gt', 'gte', 'eq', 'ne'].includes(operator);

    // For numeric operators, show the operator symbol
    if (isNumericOperator && values.length > 0) {
      const opSymbol = operatorMap[operator] || operator;
      return `${opSymbol} ${values[0]}`;
    }

    // For text filters, show values as comma-separated list
    if (values.length <= 3) {
      return values.join(', ');
    }

    return `${values.slice(0, 3).join(', ')} +${values.length - 3} more`;
  }
}
